<template>
  <Admin>
    <v-container fluid class="px-5">
      <!-- HEADER CARDS -->
      <v-row>
        <v-col cols="12" md="4">
          <v-card class="elevation-2" rounded="lg">
            <v-card-text class="d-flex align-center">
              <v-avatar color="primary lighten-4" size="48" class="mr-4">
                <v-icon color="primary">mdi-google-classroom</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-uppercase grey--text font-weight-bold">Class</div>
                <div class="text-h6 font-weight-bold text--primary">{{ schoolClass.name }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="elevation-2" rounded="lg">
            <v-card-text class="d-flex align-center">
              <v-avatar color="secondary lighten-4" size="48" class="mr-4">
                <v-icon color="secondary">mdi-domain</v-icon>
              </v-avatar>
              <div>
                <div class="text-caption text-uppercase grey--text font-weight-bold">School</div>
                <div class="text-h6 font-weight-bold text--primary">{{ schoolClass.school.name }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="elevation-2" rounded="lg">
            <v-card-text class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-avatar color="info lighten-4" size="48" class="mr-4">
                  <v-icon color="info">mdi-account-group</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption text-uppercase grey--text font-weight-bold">Students</div>
                  <div class="text-h6 font-weight-bold text--primary">{{ schoolClass.students.length }}</div>
                </div>
              </div>
              
              <!-- Invite Button & Dialog -->
              <v-dialog v-model="dialog" persistent max-width="500px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="primary" elevation="2" v-bind="attrs" v-on="on">
                    <v-icon left>mdi-account-plus</v-icon> Invite
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title class="headline primary white--text">
                    Invite Student
                  </v-card-title>
                  <v-card-text class="pt-6">
                    <v-form ref="form" v-model="valid" lazy-validation>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field outlined dense color="primary" label="Student Name" v-model="student.name" :rules="nameRules"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field outlined dense color="primary" label="Student Email" v-model="student.email" :rules="emailRules" :error-messages="student.error" @blur="checkStudentExistence" @focus="student.error = ''"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field outlined dense color="primary" label="Parent Name" v-model="student.parent_name" :rules="nameRules"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field outlined dense color="primary" label="Parent Email" v-model="student.parent_email" :rules="emailRules"></v-text-field>
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-card-text>
                  <v-card-actions class="pb-4 px-6">
                    <v-spacer></v-spacer>
                    <v-btn color="grey darken-1" text @click="clear">Cancel</v-btn>
                    <v-btn color="primary" elevation="2" @click="save" :loading="isInviting">Send Invite</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- SKILLS GRID -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card class="elevation-2" rounded="lg">
            <v-card-title class="pb-2">
              <v-icon left color="primary">mdi-format-list-checks</v-icon> Class Skills
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pt-6">
              <v-row>
                <v-col cols="12" sm="6" md="4" lg="3" v-for="skill in skills" :key="skill.id">
                  <v-card outlined class="d-flex align-center px-4 py-2" style="border-width: 2px" :style="skill.enabled ? 'border-color: #e31e24 !important;' : ''">
                    <v-checkbox
                      v-model="skill.enabled"
                      :label="skill.name"
                      color="primary"
                      hide-details
                      class="mt-0 pt-0"
                      @click="toggleSkill(skill.id)"
                    ></v-checkbox>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- STUDENTS DATA TABLE -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card class="elevation-2" rounded="lg">
            <v-card-title>
              <v-icon left color="primary">mdi-account-multiple</v-icon> Students List
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search students..."
                single-line
                hide-details
                outlined
                dense
                style="max-width: 300px;"
              ></v-text-field>
            </v-card-title>
            
            <v-data-table
              :headers="studentHeaders"
              :items="schoolClass.students"
              :search="search"
              class="elevation-0"
              :items-per-page="10"
            >
              <template v-slot:item.parent_consent="{ item }">
                <v-chip
                  v-if="!!item && !!item.parent && !!item.parent.name && !!item.parent.email"
                  :color="item.parent_consent ? 'success' : 'warning'"
                  small
                  outlined
                >
                  {{ item.parent_consent ? "Approved" : "Waiting" }}
                </v-chip>
                <span v-else class="text-caption grey--text">N/A</span>
              </template>
              
              <template v-slot:item.last_activity>
                <span class="text-caption grey--text">tbd</span>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="error"
                      v-bind="attrs"
                      v-on="on"
                      @click="deleteStudent({ studentIndex: schoolClass.students.indexOf(item), studentId: item.id })"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Remove Student</span>
                </v-tooltip>
              </template>

              <template v-slot:no-data>
                <div class="text-center py-4 grey--text">
                  Empty
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </Admin>
</template>

<script>
import Admin from "../../layouts/Admin";
import { mapActions, mapGetters } from "vuex";
import axios from "axios";

export default {
  components: {
    Admin,
  },
  data() {
    return {
      dialog: false,
      valid: true,
      student: {
        name: "",
        email: "",
        parent_name: "",
        parent_email: "",
        error: "",
      },
      isInviting: false,
      nameRules: [(v) => !!v || "Name is required"],
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],
      search: "",
      studentHeaders: [
        { text: 'Name', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Parent Consent', value: 'parent_consent' },
        { text: 'Last Activity', value: 'last_activity' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters("schoolClass", ["schoolClass"]),
    ...mapGetters("skill", ["skills"]),
    studentsCount() {
      return this.schoolClass.students.length;
    },
  },
  methods: {
    ...mapActions("schoolClass", [
      "fetchClass",
      "deleteStudent",
      "inviteStudent",
    ]),
    ...mapActions("skill", ["fetchSkills", "toggleSkill"]),
    checkStudentExistence() {
      return axios
        .get("/api/users/exists?email=" + this.student.email)
        .then(
          (res) =>
            (this.student.error = res.data
              ? "User with such email already exist"
              : "")
        );
    },
    async save() {
      this.isInviting = true;
      try {
        await this.checkStudentExistence();
        if (this.$refs.form.validate() && !this.student.error) {
          await this.inviteStudent(this.student);
          this.clear();
        }
      } finally {
        this.isInviting = false;
      }
    },
    clear() {
      this.dialog = false;
      this.student.name = "";
      this.student.email = "";
      this.student.parent_name = "";
      this.student.parent_email = "";
      this.student.error = "";
      this.$refs.form.resetValidation();
    },
  },
  mounted() {
    this.fetchClass();
    this.fetchSkills();
  },
};
</script>
